import React from "react"
import {
  Flex, HStack, Stack, Text,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import GlobeIcon from "components/elements/GlobeIcon"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import LinkField from "components/elements/LinkField"
import { useForms } from "queries/forms"
import useModal from "services/useModal"
import { DioboxEvent, ModalName } from "sharedTypes"
import FallbackIcon from "./FallbackIcon"
import { chosenTarget, formNote, webPageNote } from "./destinations"

type Props = {
  event: DioboxEvent
  canEdit?: boolean
  compact?: boolean
}

const formLabel = (name?: string) => {
  if (!name) return "Form"

  return /\bform$/i.test(name.trim()) ? name : `${name} Form`
}

const EventLinkDetails = ({ event, canEdit = true, compact }: Props) => {
  const showModal = useModal()
  const target = chosenTarget(event)
  const { data } = useForms(event.id, { enabled: target === "form" })
  const { urls } = event

  if (!urls) {
    return null
  }

  const form = (data?.data ?? []).find(({ id }) => id === event.rootFormId)
  const destination = {
    site: { name: "Web Page", url: urls.site, note: webPageNote(event) },
    portal: { name: "Event Portal", url: urls.portal, note: undefined },
    form: { name: formLabel(form?.name), url: form?.url, note: formNote(form, event) },
    none: undefined,
  }[target]

  return (
    <Stack spacing={5}>
      <LinkField url={urls.root} withOpen={compact} />
      <Flex justify="space-between" align="center" gap={4}>
        {destination ? (
          <HStack spacing={1.5}>
            <Text>
              Opens:{" "}
              {destination.note ? (
                <Link href={urls.portal} isExternal underlined={!compact}>Event Portal</Link>
              ) : destination.url ? (
                <Link href={destination.url} isExternal underlined={!compact}>
                  {destination.name}
                </Link>
              ) : destination.name}
            </Text>
            {destination.note && <FallbackIcon note={destination.note} />}
          </HStack>
        ) : (
          <HStack spacing={2} color="gray.600">
            <Icon icon="cancel-circle" size={4} />
            <Text>The Event Link is disabled.</Text>
          </HStack>
        )}
        {canEdit && (
          <Button
            variant="outline"
            leftIcon={<GlobeIcon boxSize={4} />}
            onClick={() => showModal(ModalName.EventLink)}
            {...compact && { size: "sm", colorScheme: "gray", flexShrink: 0 }}
          >
            Link Settings
          </Button>
        )}
      </Flex>
    </Stack>
  )
}

export default EventLinkDetails
